import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { ScrollText } from "lucide-react";
import { useMemo } from "react";
import { auditInfinite } from "../../api/ops";
import type { OpsAuditEntry } from "../../api/types";
import { DataTable } from "../../components/DataTable";
import { Button, Card, EmptyState, ErrorState, Loading, PageHeader } from "../../components/ui";
import { fmtDateTime } from "../../lib/format";

export const Route = createFileRoute("/_app/audit")({
  staticData: { crumb: { label: "Audit", parents: [] } },
  component: AuditPage,
});

const col = createColumnHelper<OpsAuditEntry>();

/** "resolution: fixed, version: 0.2.24". */
function detailText(d: Record<string, unknown> | null | undefined): string {
  if (!d) return "";
  return Object.entries(d)
    .filter(([, v]) => v !== null && v !== undefined && v !== "")
    .map(
      ([k, v]) =>
        `${k.replace(/_/g, " ")}: ${Array.isArray(v) ? v.join(", ") : typeof v === "object" ? JSON.stringify(v) : String(v)}`,
    )
    .join(", ");
}

function AuditPage() {
  const q = useInfiniteQuery(auditInfinite);
  const rows = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);
  const columns = useMemo(
    () => [
      col.accessor("at", {
        header: "When",
        enableSorting: false,
        cell: (c) => <span className="tnum whitespace-nowrap">{fmtDateTime(c.getValue())}</span>,
      }),
      col.accessor("actor", {
        header: "Who",
        enableSorting: false,
        cell: (c) =>
          c.getValue() === "cli" ? <span className="text-ink-2">Command line</span> : c.getValue(),
      }),
      col.accessor("action", {
        header: "Action",
        enableSorting: false,
        cell: (c) => <span className="font-mono text-caption">{c.getValue()}</span>,
      }),
      col.accessor((a) => a.company?.name ?? "", {
        id: "company",
        header: "Company",
        enableSorting: false,
        cell: (c) => <span className="whitespace-nowrap">{c.getValue()}</span>,
      }),
      col.accessor((a) => a.target_id ?? "", {
        id: "target",
        header: "Target",
        enableSorting: false,
        cell: (c) => (
          <span
            className="block max-w-[220px] truncate font-mono text-caption text-ink-2"
            title={c.getValue()}
          >
            {c.getValue()}
          </span>
        ),
      }),
      col.accessor((a) => detailText(a.data), {
        id: "detail",
        header: "Detail",
        enableSorting: false,
        cell: (c) => <span className="text-ink-2">{c.getValue()}</span>,
      }),
    ],
    [],
  );

  return (
    <>
      <PageHeader title="Audit" />
      <Card>
        {q.isPending ? (
          <Loading />
        ) : q.isError ? (
          <ErrorState error={q.error} onRetry={() => q.refetch()} />
        ) : (
          <>
            <DataTable
              data={rows}
              columns={columns}
              getRowId={(a) => a.id}
              empty={<EmptyState icon={<ScrollText />} title="Nothing recorded yet" />}
            />
            {q.hasNextPage ? (
              <div className="flex justify-center border-hairline border-t p-3">
                <Button size="sm" onClick={() => q.fetchNextPage()} busy={q.isFetchingNextPage}>
                  Load more
                </Button>
              </div>
            ) : null}
          </>
        )}
      </Card>
    </>
  );
}
